import { v4 as uuid } from "uuid";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import AudioInstance from "../../api/AudioApi";
import VisualMediaInstance from "../../api/VisualMediaApi";
import {
  EncodeStatusEnum,
  FolderTypeEnum,
  IAudio,
  IVisualMedia,
  VisualMediaClassificationEnum,
} from "../../types/models";
import {
  IPendingUploadItem,
  IScheduledShow,
  IStreamPlaylistType,
  IStreamSelectedItem,
} from "../../types/interfaces";
import axios from "axios";
import { RootState } from "../store";
import { UploadApi } from "../../api/UploadApi";
import type { UploadReceipt } from "../../api/UploadApi";
import { UploadLifetime, savedUploads, rememberUpload, forgetUpload, liveUpload, registerLiveUpload } from "../../helpers/UploadLifetime";
import { Uploader } from "../../helpers/Uploader";
import { notification } from "antd";
import { dispatchErrorNotification } from "../../helpers/errorDispatch";
import { saveWithRetry } from "../../helpers/saveWithRetry";
import { ApiError, translateApiError } from "../../i18n/errorCodes";
import i18n from "../../i18n";
import {
  getLastOrder,
  parseJingleItemId,
  parseSecondaryItemId,
  parseShowItemId,
} from "../../helpers/helpers";
import {
  JINGLE_ITEM_PREFIX,
  MAX_FILE_SIZE_BYTES,
  SECONDARY_ITEM_PREFIX,
  SHOW_ITEM_PREFIX,
} from "../../constants/constants";
import { refreshStreamSnapshot } from "../refreshStreamSnapshot";
import { formatBytes } from "../../helpers/bytes";

export interface MediaListIdentity {
  streamId: string;
  type: FolderTypeEnum;
  folderId?: string;
  query: string;
  pageNumber: number;
  classification?: VisualMediaClassificationEnum;
}

export const mediaListContextKey = (identity: MediaListIdentity) =>
  JSON.stringify([
    identity.streamId,
    identity.type,
    identity.folderId ?? null,
    identity.query,
    identity.pageNumber,
    identity.classification ?? null,
  ]);

interface MediaState {
  importItems: any[];
  loading: boolean;
  getItemsLoading: boolean;
  itemsStreamId: string | null;
  itemsContextKey: string | null;
  itemsRequestId: string | null;
  itemsError: string | null;
  uploadedItem: IAudio | IVisualMedia | null;
  msg: string;
  error: string;
  items: IAudio[] | IVisualMedia[];
  hasMore: boolean;
  searchResult: IAudio[] | IVisualMedia[];
  isSearching: boolean;
  UploadItemLoading: boolean;
  currentItemUploadingId: string | null;
  uploadItemProgress: number;
  pendingUploads: IPendingUploadItem[];
  selectedItems: IStreamSelectedItem[];
  selectedJingleItems: IStreamSelectedItem[];
  selectedSecondaryItems: IStreamSelectedItem[];
  autoAddToSelected: boolean;
  checkboxItemsIds: string[];
  backgrounds: IVisualMedia[];
  getBackgroundsLoading: boolean;
  selectedBackgroundId: string | null;
  uploadBackgroundProgress: number;
  overlaysLoading: boolean;
  scheduledShows: IScheduledShow[];
  visiblePlaylistType: IStreamPlaylistType;
  uploadQueue: string[];
  concurrentUploads: number;
  lastAddedItemId: string | null;
  encodeProgress: Record<string, number>;
}

function selectedWithCurrentMedia(
  selected: IStreamSelectedItem,
  media: IAudio | IVisualMedia,
): IStreamSelectedItem {
  const common = { ...selected, duration: media.duration };
  if (selected.type !== "video" || !("encodeStatus" in media)) return common;
  return {
    ...common,
    thubmnailUrl: media.thubmnailUrl,
    thumbnailError: media.thumbnailError,
    width: media.width,
    height: media.height,
    resolutionTier: media.resolutionTier,
    resolutionError: media.resolutionError,
    encodeStatus: media.encodeStatus,
    encodeError: media.encodeError,
  };
}

const initialState: MediaState = {
  importItems: [],
  loading: false,
  getItemsLoading: false,
  itemsStreamId: null,
  itemsContextKey: null,
  itemsRequestId: null,
  itemsError: null,
  uploadedItem: null,
  msg: "",
  error: "",
  items: [],
  hasMore: false,
  searchResult: [],
  isSearching: false,
  UploadItemLoading: false,
  currentItemUploadingId: null,
  uploadItemProgress: 0,
  pendingUploads: [],
  selectedItems: [],
  selectedJingleItems: [],
  selectedSecondaryItems: [],
  autoAddToSelected: false,
  checkboxItemsIds: [],
  backgrounds: [],
  getBackgroundsLoading: false,
  selectedBackgroundId: null,
  uploadBackgroundProgress: 0,
  overlaysLoading: false,
  scheduledShows: [],
  visiblePlaylistType: IStreamPlaylistType.primary,
  uploadQueue: [],
  concurrentUploads: 3,
  lastAddedItemId: null,
  encodeProgress: {},
};

export const mediaSlice = createSlice({
  name: "audio",
  initialState,
  reducers: {
    setEncodeProgress(state, action: PayloadAction<{ visualMediaId: string; percent: number }>) {
      const isActive = [...state.items, ...state.backgrounds, ...state.selectedItems].some(
        (item) => item.id === action.payload.visualMediaId &&
          "encodeStatus" in item && ["queued", "running"].includes(String(item.encodeStatus)),
      );
      if (isActive)
        state.encodeProgress[action.payload.visualMediaId] = action.payload.percent;
    },
    applyEncodeStatus(
      state,
      action: PayloadAction<{ visualMediaId: string; encodeStatus: string; encodeError: string | null }>,
    ) {
      const { visualMediaId, encodeStatus, encodeError } = action.payload;
      const patch = (m: IAudio | IVisualMedia) => {
        if (m.id === visualMediaId && "encodeStatus" in m) {
          (m as IVisualMedia).encodeStatus = encodeStatus as EncodeStatusEnum;
          (m as IVisualMedia).encodeError = encodeError;
        }
      };
      state.items.forEach(patch);
      state.searchResult.forEach(patch);
      state.backgrounds.forEach(patch);
      const selected = state.selectedItems.find((m) => m.id === visualMediaId && m.type === "video");
      if (selected) {
        selected.encodeStatus = encodeStatus as EncodeStatusEnum;
        selected.encodeError = encodeError;
      }
      if (["succeeded", "failed", "cancelled", "none"].includes(encodeStatus)) {
        delete state.encodeProgress[visualMediaId];
      }
    },
    clearEncodeProgress(state) {
      state.encodeProgress = {};
    },
    setVisiblePlaylistType(state, action: PayloadAction<IStreamPlaylistType>) {
      state.visiblePlaylistType = action.payload;
    },
    setOverlaysLoading(state, action: PayloadAction<boolean>) {
      state.overlaysLoading = action.payload;
    },
    setBackgroundsLoading(state, action: PayloadAction<boolean>) {
      state.getBackgroundsLoading = action.payload;
    },
    setBackgrounds(state, action: PayloadAction<IVisualMedia[]>) {
      state.backgrounds = action.payload;
    },
    addBackgroundToList(state, action: PayloadAction<IVisualMedia>) {
      state.backgrounds = [action.payload, ...state.backgrounds];
    },
    setSelectedBackgroundId(state, action: PayloadAction<string | null>) {
      state.selectedBackgroundId = action.payload;
    },
    setUploadBackgroundProgress(state, action: PayloadAction<number>) {
      state.uploadBackgroundProgress = action.payload;
    },
    onDeleteBackgroundSuccess(state, action: PayloadAction<string>) {
      state.backgrounds = state.backgrounds.filter(
        (background) => background.id !== action.payload,
      );
      if (state.selectedBackgroundId === action.payload) {
        if (state.backgrounds.length) {
          state.selectedBackgroundId = state.backgrounds[0].id;
        } else {
          state.selectedBackgroundId = null;
        }
      }
    },
    setGetItemLoading(state, action: PayloadAction<boolean>) {
      state.getItemsLoading = action.payload;
    },
    beginItemsRequest(
      state,
      action: PayloadAction<{
        streamId: string;
        contextKey: string;
        requestId: string;
        background?: boolean;
      }>,
    ) {
      if (state.itemsContextKey !== action.payload.contextKey) state.items = [];
      state.itemsStreamId = action.payload.streamId;
      state.itemsContextKey = action.payload.contextKey;
      state.itemsRequestId = action.payload.requestId;
      state.itemsError = null;
      state.getItemsLoading = !action.payload.background;
    },
    completeItemsRequest(
      state,
      action: PayloadAction<{
        streamId: string;
        contextKey: string;
        requestId: string;
        items: IAudio[] | IVisualMedia[];
      }>,
    ) {
      if (
        state.itemsStreamId === action.payload.streamId &&
        state.itemsContextKey === action.payload.contextKey &&
        state.itemsRequestId === action.payload.requestId
      ) {
        state.items = action.payload.items;
        const byId = new Map(action.payload.items.map((item) => [item.id, item]));
        state.selectedItems = state.selectedItems.map((selected) => {
          const current = byId.get(selected.id);
          return current ? selectedWithCurrentMedia(selected, current) : selected;
        });
        // Progress is ephemeral, and a terminal authoritative row clears stale percentages.
        const active = new Set(
          [...action.payload.items, ...state.selectedItems]
            .filter((item) =>
              "encodeStatus" in item && ["queued", "running"].includes(String(item.encodeStatus))
            )
            .map((item) => item.id),
        );
        for (const id of Object.keys(state.encodeProgress)) if (!active.has(id)) delete state.encodeProgress[id];
      }
    },
    failItemsRequest(
      state,
      action: PayloadAction<{
        streamId: string;
        contextKey: string;
        requestId: string;
        error: string;
      }>,
    ) {
      if (
        state.itemsStreamId === action.payload.streamId &&
        state.itemsContextKey === action.payload.contextKey &&
        state.itemsRequestId === action.payload.requestId
      )
        state.itemsError = action.payload.error;
    },
    finishItemsRequest(
      state,
      action: PayloadAction<{
        streamId: string;
        contextKey: string;
        requestId: string;
      }>,
    ) {
      if (
        state.itemsStreamId === action.payload.streamId &&
        state.itemsContextKey === action.payload.contextKey &&
        state.itemsRequestId === action.payload.requestId
      ) {
        state.getItemsLoading = false;
        state.itemsRequestId = null;
      }
    },
    onItemSearch(state) {
      state.isSearching = true;
      state.items = [];
    },
    addItemToList(state, action: PayloadAction<IStreamSelectedItem>) {
      state.selectedItems.push(action.payload);
    },
    appendItemToList(
      state,
      action: PayloadAction<Omit<IStreamSelectedItem, "order">>,
    ) {
      const lastOrder = getLastOrder(state.selectedItems);
      state.selectedItems.push({ ...action.payload, order: lastOrder + 1 });
      state.lastAddedItemId = action.payload.id;
    },
    appendItemToJingleList(
      state,
      action: PayloadAction<Omit<IStreamSelectedItem, "order">>,
    ) {
      const lastOrder = getLastOrder(state.selectedJingleItems);
      state.selectedJingleItems.push({
        ...action.payload,
        order: lastOrder + 1,
      });
      state.lastAddedItemId = action.payload.id;
    },
    appendItemToSecondaryList(
      state,
      action: PayloadAction<Omit<IStreamSelectedItem, "order">>,
    ) {
      const lastOrder = getLastOrder(state.selectedSecondaryItems);
      state.selectedSecondaryItems.push({
        ...action.payload,
        order: lastOrder + 1,
      });
      state.lastAddedItemId = action.payload.id;
    },
    setLastAddedItemId(state, action: PayloadAction<string>) {
      state.lastAddedItemId = action.payload;
    },
    clearLastAddedItemId(state) {
      state.lastAddedItemId = null;
    },
    setCheckboxItemsIds(state, action: PayloadAction<string[]>) {
      state.checkboxItemsIds = action.payload;
    },
    updateSelectedItemChecked(
      state,
      action: PayloadAction<IStreamSelectedItem>,
    ) {
      const item = action.payload;
      if (item.id.startsWith(SHOW_ITEM_PREFIX)) {
        const { showId } = parseShowItemId(item.id);
        const showIndex = state.scheduledShows.findIndex(
          (show) => show.id === showId,
        );
        if (showIndex > -1) {
          const show = state.scheduledShows[showIndex];
          const itemIndex = show.items.findIndex((it) => it.id === item.id);
          if (itemIndex > -1) {
            state.scheduledShows[showIndex].items[itemIndex].isChecked =
              !state.scheduledShows[showIndex].items[itemIndex].isChecked;
          }
        }
      } else if (item.id.startsWith(SECONDARY_ITEM_PREFIX)) {
        const selectedItemIndex = state.selectedSecondaryItems.findIndex(
          (audio) => audio.id === action.payload.id,
        );
        if (selectedItemIndex > -1) {
          state.selectedSecondaryItems[selectedItemIndex].isChecked =
            !state.selectedSecondaryItems[selectedItemIndex].isChecked;
        }
      } else {
        const selectedItemIndex = state.selectedItems.findIndex(
          (audio) => audio.id === action.payload.id,
        );
        if (selectedItemIndex > -1) {
          state.selectedItems[selectedItemIndex].isChecked =
            !state.selectedItems[selectedItemIndex].isChecked;
        }
      }
    },
    clearAllSelectedItemsChecked(state) {
      state.selectedItems = state.selectedItems.map((item) => ({
        ...item,
        isChecked: false,
      }));
    },
    clearAllSelectedSecondaryItemsChecked(state) {
      state.selectedSecondaryItems = state.selectedSecondaryItems.map(
        (item) => ({
          ...item,
          isChecked: false,
        }),
      );
    },
    setAllSelectedItemsChecked(state) {
      state.selectedItems = state.selectedItems.map((item) => ({
        ...item,
        isChecked: true,
      }));
    },
    setAllSelectedSecondaryItemsChecked(state) {
      state.selectedSecondaryItems = state.selectedSecondaryItems.map(
        (item) => ({
          ...item,
          isChecked: true,
        }),
      );
    },
    addMultipleItemsToList(
      state,
      action: PayloadAction<IStreamSelectedItem[]>,
    ) {
      state.selectedItems = state.selectedItems.concat(action.payload);
    },
    addMultipleItemsToJingleList(
      state,
      action: PayloadAction<IStreamSelectedItem[]>,
    ) {
      state.selectedJingleItems = state.selectedJingleItems.concat(
        action.payload,
      );
    },
    addMultipleItemsToSecondaryList(
      state,
      action: PayloadAction<IStreamSelectedItem[]>,
    ) {
      state.selectedSecondaryItems = state.selectedSecondaryItems.concat(
        action.payload,
      );
    },
    removeItemFromList(state, action: PayloadAction<IStreamSelectedItem>) {
      if (action.payload.id.startsWith(SHOW_ITEM_PREFIX)) {
        const { showId } = parseShowItemId(action.payload.id);
        const showIndex = state.scheduledShows.findIndex(
          (show) => show.id === showId,
        );
        if (showIndex > -1) {
          const items = state.scheduledShows[showIndex].items;
          const itemIndex = items.findIndex(
            (it) => it.id === action.payload.id,
          );
          if (itemIndex > -1) {
            items.splice(itemIndex, 1);
            // Re-number only items after the removed index
            for (let i = itemIndex; i < items.length; i++) {
              items[i].order = i + 1;
            }
          }
        }
      } else if (action.payload.id.startsWith(JINGLE_ITEM_PREFIX)) {
        const items = state.selectedJingleItems;
        const idx = items.findIndex((a) => a.id === action.payload.id);
        if (idx > -1) {
          items.splice(idx, 1);
          for (let i = idx; i < items.length; i++) {
            items[i].order = i + 1;
          }
        }
      } else if (action.payload.id.startsWith(SECONDARY_ITEM_PREFIX)) {
        const items = state.selectedSecondaryItems;
        const idx = items.findIndex((a) => a.id === action.payload.id);
        if (idx > -1) {
          items.splice(idx, 1);
          for (let i = idx; i < items.length; i++) {
            items[i].order = i + 1;
          }
        }
      } else {
        const items = state.selectedItems;
        const idx = items.findIndex((a) => a.id === action.payload.id);
        if (idx > -1) {
          items.splice(idx, 1);
          for (let i = idx; i < items.length; i++) {
            items[i].order = i + 1;
          }
        }
      }
    },
    removeItemFromJingleList(
      state,
      action: PayloadAction<IStreamSelectedItem>,
    ) {
      const newItems = state.selectedJingleItems.filter(
        (audio) => audio.id !== action.payload.id,
      );
      newItems.sort((a, b) => a.order - b.order);
      state.selectedJingleItems = newItems.map((item, index) => ({
        ...item,
        order: index + 1,
      }));
    },
    removeItemFromSecondaryList(
      state,
      action: PayloadAction<IStreamSelectedItem>,
    ) {
      const newItems = state.selectedSecondaryItems.filter(
        (audio) => audio.id !== action.payload.id,
      );
      newItems.sort((a, b) => a.order - b.order);
      state.selectedSecondaryItems = newItems.map((item, index) => ({
        ...item,
        order: index + 1,
      }));
    },
    removeAllItemsFromList(state) {
      state.selectedItems = [];
    },
    updateUploadItemProgress(
      state,
      action: PayloadAction<{ progress: number; id: string }>,
    ) {
      const currentItemIndex = state.pendingUploads.findIndex(
        (audio) => audio.id === action.payload.id,
      );
      if (currentItemIndex > -1) {
        state.pendingUploads[currentItemIndex].progress =
          action.payload.progress;
      }
      state.uploadItemProgress = action.payload.progress;
      state.currentItemUploadingId = action.payload.id;
    },
    setCurrentItemUploadingId(state, action: PayloadAction<string | null>) {
      state.currentItemUploadingId = action.payload;
    },
    removePendingUploadItem(state, action: PayloadAction<string>) {
      state.pendingUploads = state.pendingUploads.filter(
        (audio) => audio.id !== action.payload,
      );
    },
    clearPendingUploads(state) {
      state.pendingUploads = state.pendingUploads.filter(upload => upload.releasePending || upload.status === 'uploading' || upload.status === 'pending');
      state.uploadQueue = [];
    },
    setItemPendingUploads(state, action: PayloadAction<IPendingUploadItem[]>) {
      state.pendingUploads = action.payload;
    },
    onUploadFinished(
      state,
      action: PayloadAction<{
        item: IAudio | IVisualMedia;
        addToItems: boolean;
        type: "audio" | "video";
      }>,
    ) {
      state.loading = false;
      state.UploadItemLoading = false;
      state.uploadedItem = action.payload.item;

      if (action.payload.addToItems) {
        state.items = [action.payload.item, ...state.items];
      }
      if (state.autoAddToSelected) {
        const lastOrder = getLastOrder(state.selectedItems);
        state.selectedItems.push({
          ...action.payload.item,
          type: action.payload.type,
          order: lastOrder + 1,
        });
      }
    },
    setItem(state, action: PayloadAction<IVisualMedia | IAudio>) {
      if (state.items.some((item) => item.id === action.payload.id)) {
        state.items = state.items.map((item) =>
          item.id === action.payload.id ? action.payload : item,
        );
      }
      if (state.backgrounds.some((item) => item.id === action.payload.id)) {
        const bgIndex = state.backgrounds.findIndex(
          (item) => item.id === action.payload.id,
        );
        if (bgIndex > -1) {
          state.backgrounds[bgIndex] = action.payload as IVisualMedia;
        }
      }
      if (state.selectedItems.some((item) => item.id === action.payload.id)) {
        const index = state.selectedItems.findIndex(
          (item) => item.id === action.payload.id,
        );
        if (index > -1) {
          state.selectedItems[index] = selectedWithCurrentMedia(
            state.selectedItems[index],
            action.payload,
          );
        }
      }
    },
    setUploadLifetime(state, action: PayloadAction<{ id: string; intentId?: string; releasePending?: boolean; releaseAfter?: string | null }>) {
      const upload = state.pendingUploads.find(u => u.id === action.payload.id);
      if (upload && !(upload.status === 'completed' && action.payload.releasePending)) Object.assign(upload, action.payload);
    },

    updateUploadStatus: (
      state,
      action: PayloadAction<{
        id: string;
        status: IPendingUploadItem["status"];
        controller?: AbortController;
      }>,
    ) => {
      const upload = state.pendingUploads.find(
        (u) => u.id === action.payload.id,
      );
      if (upload) {
        if (upload.status === 'completed' && action.payload.status !== 'completed') return;
        upload.status = action.payload.status;
        if (upload.status === "completed") { upload.releasePending = false; upload.releaseAfter = null; }
        if (action.payload.controller) {
          upload.controller = action.payload.controller;
        }
      }
    },

    addPendingUpload: (
      state,
      action: PayloadAction<Omit<IPendingUploadItem, "uploaded" | "failed">>,
    ) => {
      const newUpload: IPendingUploadItem = {
        ...action.payload,
        uploaded: false,
        failed: false,
        status: "pending",
        totalBytes: action.payload.body.size,
      };
      state.pendingUploads.push(newUpload);
    },

    setSelectedItems(state, action: PayloadAction<IStreamSelectedItem[]>) {
      state.selectedItems = action.payload;
    },
    setSelectedJingleItems(
      state,
      action: PayloadAction<IStreamSelectedItem[]>,
    ) {
      state.selectedJingleItems = action.payload;
    },
    setSelectedSecondaryItems(
      state,
      action: PayloadAction<IStreamSelectedItem[]>,
    ) {
      state.selectedSecondaryItems = action.payload;
    },
    setItems(state, action: PayloadAction<IAudio[] | IVisualMedia[]>) {
      state.items = action.payload;
      const byId = new Map(action.payload.map((item) => [item.id, item]));
      state.selectedItems = state.selectedItems.map((selected) => {
        const current = byId.get(selected.id);
        return current ? selectedWithCurrentMedia(selected, current) : selected;
      });
    },
    setAutoAddToSelected(state, action: PayloadAction<boolean>) {
      state.autoAddToSelected = action.payload;
    },
    /** UC-5.9: deleted media must leave every list the next Go Live sends, or the API refuses it. */
    pruneDeletedMedia(state, action: PayloadAction<string[]>) {
      const gone = new Set(action.payload);
      state.items = (state.items as any[]).filter((item) => !gone.has(item.id)) as any;
      state.selectedItems = state.selectedItems.filter((item) => !gone.has(item.id));
      state.selectedJingleItems = state.selectedJingleItems.filter(
        (item) => !gone.has(parseJingleItemId(item.id)),
      );
      state.selectedSecondaryItems = state.selectedSecondaryItems.filter(
        (item) => !gone.has(parseSecondaryItemId(item.id)),
      );
      state.scheduledShows = state.scheduledShows.map((show) => ({
        ...show,
        items: show.items.filter(
          (item) => !gone.has(item.id) && !gone.has(parseShowItemId(item.id).itemId),
        ),
      }));
      state.checkboxItemsIds = state.checkboxItemsIds.filter((id) => !gone.has(id));
    },
    onDuplicateSuccess(state, action: PayloadAction<IAudio | IVisualMedia>) {
      state.items = [action.payload, ...state.items];
    },
    onUpdateItemSuccess(state, action: PayloadAction<IAudio | IVisualMedia>) {
      const itemsIndex = state.items.findIndex(
        (audio) => audio.id === action.payload.id,
      );
      if (itemsIndex > -1) {
        state.items[itemsIndex] = {
          ...state.items[itemsIndex],
          ...action.payload,
        };
      }
      const selectedItemIndex = state.selectedItems.findIndex(
        (audio) => audio.id === action.payload.id,
      );
      if (selectedItemIndex > -1) {
        state.selectedItems[selectedItemIndex] = {
          ...state.selectedItems[selectedItemIndex],
          name: action.payload.name,
          author: action.payload.author,
          sizeInKB: action.payload.sizeInKB,
        };
      }
    },
    setImportItems(state, action: PayloadAction<any>) {
      state.importItems = action.payload;
    },
    addShow(state, action: PayloadAction<IScheduledShow>) {
      state.scheduledShows = [action.payload, ...state.scheduledShows];
    },
    updateShow(state, action: PayloadAction<IScheduledShow>) {
      const showIndex = state.scheduledShows.findIndex(
        (show) => show.id === action.payload.id,
      );
      if (showIndex > -1) {
        state.scheduledShows[showIndex] = { ...action.payload };
      }
    },
    removeShow(state, action: PayloadAction<string>) {
      state.scheduledShows = state.scheduledShows.filter(
        (show) => show.id !== action.payload,
      );
    },
    setShows(state, action: PayloadAction<IScheduledShow[]>) {
      state.scheduledShows = action.payload;
    },
    addItemToShowList(
      state,
      action: PayloadAction<{ showId: string; item: IStreamSelectedItem }>,
    ) {
      state.scheduledShows = state.scheduledShows.map((show) => {
        if (show.id === action.payload.showId) {
          if (
            show.items.some((item) =>
              item.id.includes(parseShowItemId(action.payload.item.id).itemId),
            )
          ) {
            notification.info({ message: i18n.t("liveStream:stepper.media.itemAlreadyInShow") });
            return show;
          }
          return {
            ...show,
            items: show.items.concat([action.payload.item]),
          };
        }
        return show;
      });
    },
    removeItemFromShowList(
      state,
      action: PayloadAction<{ showId: string; itemId: string }>,
    ) {
      state.scheduledShows = state.scheduledShows.map((show) => {
        if (show.id === action.payload.showId) {
          return {
            ...show,
            items: show.items.filter(
              (item) => item.id !== action.payload.itemId,
            ),
          };
        }
        return show;
      });
    },
    setShowItems(
      state,
      action: PayloadAction<{ showId: string; items: IStreamSelectedItem[] }>,
    ) {
      state.scheduledShows = state.scheduledShows.map((show) => {
        if (show.id === action.payload.showId) {
          return {
            ...show,
            items: action.payload.items,
          };
        }
        return show;
      });
    },
    addMultipleItemsToShowList(
      state,
      action: PayloadAction<{ showId: string; items: IStreamSelectedItem[] }>,
    ) {
      state.scheduledShows = state.scheduledShows.map((show) => {
        if (show.id === action.payload.showId) {
          const existingItemsInShowIds = action.payload.items
            .filter((item) =>
              show.items.some((it) =>
                it.id.includes(parseShowItemId(item.id).itemId),
              ),
            )
            .map((item) => item.id);
          if (existingItemsInShowIds.length) {
            notification.info({
              message:
                "Some items already exist in this show so will be skipped",
            });
          }
          return {
            ...show,
            items: show.items.concat(
              action.payload.items.filter(
                (item) => !existingItemsInShowIds.includes(item.id),
              ),
            ),
          };
        }
        return show;
      });
    },
  },
});

export const {
  setEncodeProgress,
  applyEncodeStatus,
  clearEncodeProgress,
  setVisiblePlaylistType,
  setOverlaysLoading,
  setBackgrounds,
  setBackgroundsLoading,
  addBackgroundToList,
  setSelectedBackgroundId,
  setUploadBackgroundProgress,
  onDeleteBackgroundSuccess,
  setGetItemLoading,
  beginItemsRequest,
  completeItemsRequest,
  failItemsRequest,
  finishItemsRequest,
  addItemToList,
  appendItemToList,
  appendItemToJingleList,
  appendItemToSecondaryList,
  addMultipleItemsToList,
  addMultipleItemsToJingleList,
  addMultipleItemsToSecondaryList,
  onItemSearch,
  onUploadFinished,
  removeAllItemsFromList,
  removeItemFromList,
  removeItemFromJingleList,
  removeItemFromSecondaryList,
  setItemPendingUploads,
  setItems,
  setItem,
  setAutoAddToSelected,
  setSelectedItems,
  setSelectedJingleItems,
  setSelectedSecondaryItems,
  updateUploadItemProgress,
  removePendingUploadItem,
  setCurrentItemUploadingId,
  clearPendingUploads,
  pruneDeletedMedia,
  onUpdateItemSuccess,
  setCheckboxItemsIds,
  setImportItems,
  addItemToShowList,
  addShow,
  updateShow,
  removeItemFromShowList,
  removeShow,
  setShows,
  setShowItems,
  updateSelectedItemChecked,
  clearAllSelectedItemsChecked,
  setAllSelectedItemsChecked,
  clearAllSelectedSecondaryItemsChecked,
  setAllSelectedSecondaryItemsChecked,
  addMultipleItemsToShowList,
  onDuplicateSuccess,
  setUploadLifetime,
  updateUploadStatus,
  addPendingUpload,
  setLastAddedItemId,
  clearLastAddedItemId,
} = mediaSlice.actions;

export const cancelUpload = createAsyncThunk('media/cancelUpload', async (id: string, { dispatch, getState }) => {
  const upload = (getState() as RootState).media.pendingUploads.find(u => u.id === id);
  if (!upload || upload.status === 'completed') return;
  const live = liveUpload(upload.streamId, id);
  (live?.controller ?? upload.controller)?.abort();
  dispatch(updateUploadStatus({ id, status: 'cancelled' }));
  dispatch(setUploadLifetime({ id, releasePending: true, releaseAfter: null }));
  const lifetime = live?.lifetime ?? new UploadLifetime({ requestKey: id, streamId: upload.streamId, name: upload.body.name, size: upload.body.size, contentType: upload.body.type, state: 'active', intentId: upload.intentId });
  lifetime.onCancellation = result => dispatch(setUploadLifetime({ id, ...result }));
  if (!lifetime.record.intentId) {
    try { const { data } = await UploadApi.lookup(upload.streamId, id); lifetime.record.intentId = data.intentId; }
    catch { /* Init may still be in flight; its controller retains this cancellation. */ }
  }
  await lifetime.cancel();
});
export const cancelItemUpload = cancelUpload;
export const cancelAllUploads = createAsyncThunk('media/cancelAllUploads', async (_, { dispatch, getState }) => {
  const pending = (getState() as RootState).media.pendingUploads.filter(u => u.status !== 'completed');
  await Promise.all(pending.map(u => dispatch(cancelUpload(u.id))));
});

const RELEASED_UPLOAD_STATES = ["cancelled", "expired"];
/** Heartbeats run every five minutes and a hidden tab can be throttled or skip one beat; two missed beats plus margin means no live transfer in any tab. */
export const STALE_UPLOAD_HEARTBEAT_MS = 12 * 60_000;

/** Reload restores receipts for inspection/cancellation; browser File bytes must be selected again for transfer.
 *  A receipt the user dismissed (tray cleared) is still cancelled/forgotten on the server but never re-added to the tray. */
export const restoreUploadReceipts = createAsyncThunk('media/restoreUploadReceipts', async (_, { dispatch, getState }) => {
  for (const record of savedUploads()) {
    if (record.state === 'completed' && !record.releasePending) {
      forgetUpload(record.streamId, record.requestKey);
      continue;
    }
    if (liveUpload(record.streamId, record.requestKey)) continue;
    let data: UploadReceipt;
    try {
      ({ data } = record.intentId ? await UploadApi.get(record.streamId, record.intentId) : await UploadApi.lookup(record.streamId, record.requestKey));
    } catch (error: any) {
      if (error?.response?.status === 404) {
        forgetUpload(record.streamId, record.requestKey);
        dispatch(removePendingUploadItem(record.requestKey));
      }
      continue;
    }
    if (liveUpload(record.streamId, record.requestKey)) continue;
    if (data.state === 'consumed') {
      forgetUpload(record.streamId, record.requestKey);
      dispatch(updateUploadStatus({ id: record.requestKey, status: 'completed' }));
      dispatch(setUploadLifetime({ id: record.requestKey, releasePending: false, releaseAfter: null }));
      continue;
    }
    if (RELEASED_UPLOAD_STATES.includes(data.state)) {
      forgetUpload(record.streamId, record.requestKey);
      dispatch(removePendingUploadItem(record.requestKey));
      continue;
    }
    record.intentId = data.intentId; record.key = data.key;
    const orphaned = data.state === 'active' && (record.state === 'cancelled' || !data.heartbeatAt || Date.now() - Date.parse(data.heartbeatAt) > STALE_UPLOAD_HEARTBEAT_MS);
    if (data.state === 'active' && !orphaned) continue;
    if (orphaned) {
      try {
        const result = await UploadApi.cancel(record.streamId, data.intentId);
        Object.assign(record, result.data);
      } catch {
        rememberUpload(record);
        continue;
      }
    } else {
      record.releasePending = data.releasePending; record.releaseAfter = data.releaseAfter;
    }
    record.state = 'cancelled';
    rememberUpload(record);
    const present = (getState() as RootState).media.pendingUploads.find(u => u.id === record.requestKey);
    if (!present && !record.dismissed) {
      const body = new File([], record.name, { type: record.contentType });
      Object.defineProperty(body, 'size', { value: record.size });
      dispatch(addPendingUpload({ id: record.requestKey, streamId: record.streamId, body, progress: 0, intentId: data.intentId, releasePending: record.releasePending, releaseAfter: record.releaseAfter }));
      dispatch(updateUploadStatus({ id: record.requestKey, status: 'cancelled' }));
    } else if (present) dispatch(setUploadLifetime({ id: record.requestKey, intentId: data.intentId, releasePending: record.releasePending, releaseAfter: record.releaseAfter }));
  }
});

export const uploadItem = createAsyncThunk(
  "media/uploadItem",
  async (
    { streamId, body, folderId, type }: { streamId: string; body: File; folderId?: string; type: "audio" | "video" },
    { dispatch, getState },
  ) => {
    const uploadId = uuid();
    const controller = new AbortController();

    dispatch(
      addPendingUpload({
        id: uploadId,
        streamId,
        body,
        folderId,
        progress: 0,
        status: "pending",
        controller,
        totalBytes: body.size,
      }),
    );

    if (body.size > MAX_FILE_SIZE_BYTES) {
      dispatch(updateUploadStatus({ id: uploadId, status: "failed" }));
      notification.error({ message: i18n.t("errors:UPLOAD_FILE_TOO_LARGE") });
      return;
    }

    // Client-side cap pre-check (F9). The api enforces the same rule on
    // presigned/generate and multipart/init and answers STORAGE_OVER_CAP.
    const storage = (getState() as RootState).entitlement.byStreamId[streamId]?.storage;
    if (storage) {
      const available = Math.max(0, storage.capBytes - storage.usedBytes - storage.reservedBytes);
      if (body.size > available) {
        dispatch(updateUploadStatus({ id: uploadId, status: "failed" }));
        notification.error({
          message: i18n.t("errors:STORAGE_OVER_CAP", { bytesAvailable: formatBytes(available) }),
        });
        return;
      }
    }

    return dispatch(startUpload({ id: uploadId, type, controller }));
  },
);

export const startUpload = createAsyncThunk(
  "media/startUpload",
  async (
    {
      id,
      type,
      controller,
    }: { id: string; type: "audio" | "video"; controller: AbortController },
    { dispatch, getState },
  ) => {
    const state = getState() as RootState;
    const upload = state.media.pendingUploads.find((u) => u.id === id);

    if (!upload || upload.status !== "pending") {
      return;
    }

    dispatch(updateUploadStatus({ id, status: "uploading", controller }));
    dispatch(setCurrentItemUploadingId(id));

    try {
      if (type === "audio") {
        await uploadAudioFile(upload, controller, dispatch, getState);
      } else if (type === "video") {
        await uploadVideoFile(upload, controller, dispatch, getState);
      }

      dispatch(updateUploadStatus({ id, status: "completed" }));
      dispatch(updateUploadItemProgress({ progress: 100, id }));
    } catch (error: any) {
      if (error.name === "AbortError") {
        const state = getState() as RootState;
        const currentUpload = state.media.pendingUploads.find(
          (u) => u.id === id,
        );
        // if (currentUpload?.status !== "cancelled") {
        //   dispatch(updateUploadStatus({ id, status: "paused" }));
        // }
      } else {
        if (error.message === "Upload cancelled") {
          dispatch(updateUploadStatus({ id, status: "cancelled" }));
        } else {
          dispatch(updateUploadStatus({ id, status: "failed" }));
        }
        notification.error({
          message: `Upload failed for ${upload.body.name}`,
          description:
            error instanceof ApiError
              ? translateApiError(error)
              : error.message || "Something went wrong during upload",
        });
      }
    } finally {
      dispatch(setCurrentItemUploadingId(null));
    }
  },
);

const uploadAudioFile = async (
  upload: IPendingUploadItem,
  controller: AbortController,
  dispatch: any,
  getState: any,
) => {
  const lifetime = new UploadLifetime({ requestKey: upload.id, streamId: upload.streamId, name: upload.body.name, size: upload.body.size, contentType: upload.body.type, state: 'active' });
  const unregister = registerLiveUpload(lifetime, controller);
  lifetime.onCancellation = result => dispatch(setUploadLifetime({ id: upload.id, ...result }));
  lifetime.onExpired = () => controller.abort();
  const abort = () => { void lifetime.cancel(); };
  controller.signal.addEventListener('abort', abort, { once: true });

  const config = {
    onUploadProgress: (progressEvent: { loaded?: number; total?: number }) => {
      const percentCompletedFloat =
        ((progressEvent.loaded || 0) * 100) /
        (progressEvent.total || upload.body.size);
      const percentCompleted = Math.round(percentCompletedFloat);

      if (percentCompleted > 0 && percentCompleted < 99) {
        dispatch(
          updateUploadItemProgress({
            progress: percentCompleted,
            id: upload.id,
          }),
        );
      }
    },
    timeout: 1000 * 60 * 60 * 10,
    headers: {
      "Content-Type": upload.body.type,
    },
    signal: controller.signal,
  };

  try {
    const presignedUrl = await VisualMediaInstance.generatePresignedUrl(upload.streamId, {
      fileName: upload.body.name,
      contentType: upload.body.type,
      contentLength: upload.body.size,
      requestKey: upload.id,
    });

    lifetime.bind(presignedUrl.data.intentId, presignedUrl.data.Key);
    dispatch(setUploadLifetime({ id: upload.id, intentId: presignedUrl.data.intentId }));
    if (controller.signal.aborted) { await lifetime.cancel(); throw new Error('Upload cancelled'); }
    await axios.put(presignedUrl.data.url, upload.body, config);
    if (controller.signal.aborted) throw new Error('Upload cancelled');

    const data = await saveWithRetry(
      () => AudioInstance.savePresignedAudio(upload.streamId, {
        originalName: upload.body.name,
        Key: presignedUrl.data.Key,
        folderId: upload.folderId,
      }),
      { signal: controller.signal },
    );

    const currentFolder = (getState() as RootState).folder.folderById;
    lifetime.finish();
    const audioFile = data.data;

    dispatch(
      onUploadFinished({
        item: audioFile,
        addToItems:
          (!currentFolder && !upload.folderId) ||
          upload.folderId === currentFolder?.id,
        type: "audio",
      }),
    );

    pollForMediaThumbnail("audio", upload.streamId, audioFile.id, dispatch);
  } catch (error: any) {
    if (axios.isCancel(error)) {
      throw new Error("Upload cancelled");
    }
    // A failed transfer cannot resume: release its reserved quota now, not when the lease expires.
    void lifetime.cancel();
    throw error;
  } finally { lifetime.stop(); unregister(); controller.signal.removeEventListener('abort', abort); }
};

const uploadVideoFile = async (
  upload: IPendingUploadItem,
  controller: AbortController,
  dispatch: any,
  getState: any,
) => {
  let teardown = () => {};
  return new Promise<void>((resolve, reject) => {
    const uploader = new Uploader({
      requestKey: upload.id,
      streamId: upload.streamId,
      fileName: upload.body.name,
      file: upload.body,
      contentType: upload.body.type,
    });

    const unregister = registerLiveUpload(uploader.lifetime, controller);
    uploader.lifetime.onCancellation = (result) =>
      dispatch(setUploadLifetime({ id: upload.id, ...result }));
    let percentage: undefined | number = undefined;
    let isCancelled = false;
    let saving = false;

    uploader
      .onComplete(async ({ Key, url }) => {
        if (isCancelled) {
          reject(new Error("Upload cancelled"));
          return;
        }

        saving = true;
        try {
          const data = await saveWithRetry(
            () => VisualMediaInstance.savePresigned(upload.streamId, {
              classification: VisualMediaClassificationEnum.playlistVideo,
              originalName: upload.body.name,
              Key,
              folderId: upload.folderId,
            }),
            { signal: controller.signal },
          );

          const currentFolder = (getState() as RootState).folder.folderById;
          const videoFile = data.data;

          dispatch(
            onUploadFinished({
              item: videoFile,
              addToItems:
                (!currentFolder && !upload.folderId) ||
                upload.folderId === currentFolder?.id,
              type: "video",
            }),
          );

          pollForMediaThumbnail(
            "video",
            upload.streamId,
            videoFile.id,
            dispatch,
          );

          resolve();
        } catch (error) {
          reject(error);
          throw error;
        }
      })
      .onProgress((data) => {
        if (isCancelled) return;

        const { percentage: newPercentage } = data;
        if (newPercentage !== percentage && Math.floor(newPercentage) < 99) {
          percentage = newPercentage;
          dispatch(
            updateUploadItemProgress({
              progress: percentage,
              id: upload.id,
            }),
          );
        }
      })
      .onError((err: any) => {
        if (isCancelled) {
          reject(new Error("Upload cancelled"));
        } else {
          reject(err);
        }
      });

    const abort = () => {
      isCancelled = true;
      void uploader.abort();
      if (!saving) reject(new Error("Upload cancelled"));
    };
    teardown = () => {
      unregister();
      uploader.lifetime.stop();
      controller.signal.removeEventListener("abort", abort);
    };
    if (controller.signal.aborted) {
      isCancelled = true;
      reject(new Error("Upload cancelled"));
      return;
    }

    controller.signal.addEventListener("abort", abort, { once: true });
    uploader.start();
  }).finally(() => teardown());
};

export const processUploadQueue = createAsyncThunk(
  "media/processUploadQueue",
  async (_, { dispatch, getState }) => {
    const state = getState() as RootState;
    const { pendingUploads, concurrentUploads } = state.media;

    if (pendingUploads.length === 0) return;

    const pendingItems = pendingUploads.filter(
      (upload) => upload.status === "pending",
    );

    const activeItems = pendingUploads.filter(
      (upload) => upload.status === "uploading",
    );

    const availableSlots = Math.max(0, concurrentUploads - activeItems.length);

    for (let i = 0; i < Math.min(availableSlots, pendingItems.length); i++) {
      const upload = pendingItems[i];
      const controller = new AbortController();

      dispatch(
        startUpload({
          id: upload.id,
          type: upload.body.type.includes("audio") ? "audio" : "video",
          controller,
        }),
      );
    }
  },
);

export const updateAudio = createAsyncThunk(
  "media/updateAudio",
  async (
    { streamId, data }: { streamId: string; data: { id: string; name: string; author: string } },
    { dispatch },
  ) => {
    dispatch(setGetItemLoading(true));
    try {
      const response = await AudioInstance.updateAudio(streamId, data);
      dispatch(onUpdateItemSuccess(response.data));
    } catch (err: any) {
      console.log(err);
      dispatchErrorNotification(err);
    }
    dispatch(setGetItemLoading(false));
  },
);

export const getAudioList = createAsyncThunk(
  "media/getAudioList",
  async (
    {
      streamId,
      query = "",
      pageNumber,
      folderId,
      background = false,
    }: {
      streamId: string;
      query?: string;
      pageNumber: number;
      folderId?: string;
      background?: boolean;
    },
    { dispatch, getState, requestId },
  ) => {
    const contextKey = mediaListContextKey({
      streamId,
      type: FolderTypeEnum.audio,
      folderId,
      query,
      pageNumber,
    });
    dispatch(beginItemsRequest({ streamId, contextKey, requestId, background }));
    try {
      const response = await AudioInstance.getAudioList(streamId, {
        query,
        pageNumber,
        folderId,
      });
      dispatch(
        completeItemsRequest({
          streamId,
          contextKey,
          requestId,
          items: response.data.data,
        }),
      );
    } catch (err: any) {
      dispatch(
        failItemsRequest({
          streamId,
          contextKey,
          requestId,
          error: err?.response?.data?.message || err?.message || "Unable to load media",
        }),
      );
      const state = (getState() as RootState).media;
      if (
        !background &&
        state.itemsContextKey === contextKey &&
        state.itemsRequestId === requestId
      )
        dispatchErrorNotification(err);
    } finally {
      dispatch(finishItemsRequest({ streamId, contextKey, requestId }));
    }
  },
);

export const getAudioById = createAsyncThunk(
  "media/getAudioById",
  async ({ streamId, id }: { streamId: string; id: string }, { dispatch }) => {
    try {
      const response = await AudioInstance.getAudioById(streamId, id);
      dispatch(setItem(response.data));
    } catch (err: any) {
      dispatchErrorNotification(err);
    }
    dispatch(setGetItemLoading(false));
  },
);

export const deleteAudio = createAsyncThunk(
  "media/deleteAudio",
  async ({ streamId, id }: { streamId: string; id: string }, { dispatch }) => {
    dispatch(setGetItemLoading(true));
    try {
      await AudioInstance.deleteAudio(streamId, id);
      dispatch(pruneDeletedMedia([id]));
      dispatch(refreshStreamSnapshot(streamId));
    } catch (err: any) {
      console.log({ err });
      dispatchErrorNotification(err);
    }
    dispatch(setGetItemLoading(false));
  },
);

export const duplicateAudio = createAsyncThunk(
  "media/duplicateAudio",
  async ({ streamId, id }: { streamId: string; id: string }, { dispatch }) => {
    dispatch(setGetItemLoading(true));
    try {
      const data = await AudioInstance.duplicateAudio(streamId, id);
      dispatch(onDuplicateSuccess(data.data));
    } catch (err: any) {
      console.log({ err });
      dispatchErrorNotification(err);
    }
    dispatch(setGetItemLoading(false));
  },
);

export const duplicateVisualMedia = createAsyncThunk(
  "media/duplicateVisualMedia",
  async ({ streamId, id }: { streamId: string; id: string }, { dispatch }) => {
    dispatch(setGetItemLoading(true));
    try {
      const data = await VisualMediaInstance.duplicateVisualMedia(streamId, id);
      dispatch(onDuplicateSuccess(data.data));
    } catch (err: any) {
      console.log({ err });
      dispatchErrorNotification(err);
    }
    dispatch(setGetItemLoading(false));
  },
);

export const updateVisualMedia = createAsyncThunk(
  "media/updateVisualMedia",
  async (
    { streamId, data }: { streamId: string; data: { id: string; name: string; author: string; thumbnailSecond?: number } },
    { dispatch },
  ) => {
    dispatch(setGetItemLoading(true));
    try {
      const response = await VisualMediaInstance.updateVisualMedia(streamId, data);
      dispatch(onUpdateItemSuccess(response.data));
    } catch (err: any) {
      console.log(err);
      dispatchErrorNotification(err);
    }
    dispatch(setGetItemLoading(false));
  },
);

export const getVisualMediaList = createAsyncThunk(
  "media/getVisualMediaList",
  async (
    {
      streamId,
      query = "",
      pageNumber,
      folderId,
      classification,
      background = false,
    }: {
      streamId: string;
      query?: string;
      pageNumber: number;
      folderId?: string;
      classification: VisualMediaClassificationEnum;
      background?: boolean;
    },
    { dispatch, getState, requestId },
  ) => {
    const contextKey = mediaListContextKey({
      streamId,
      type: FolderTypeEnum.video,
      folderId,
      query,
      pageNumber,
      classification,
    });
    dispatch(beginItemsRequest({ streamId, contextKey, requestId, background }));
    try {
      const response = await VisualMediaInstance.getVisualMediaList(streamId, {
        query,
        pageNumber,
        folderId,
        classification,
      });
      dispatch(
        completeItemsRequest({
          streamId,
          contextKey,
          requestId,
          items: response.data.data,
        }),
      );
    } catch (err: any) {
      dispatch(
        failItemsRequest({
          streamId,
          contextKey,
          requestId,
          error: err?.response?.data?.message || err?.message || "Unable to load media",
        }),
      );
      const state = (getState() as RootState).media;
      if (
        !background &&
        state.itemsContextKey === contextKey &&
        state.itemsRequestId === requestId
      )
        dispatchErrorNotification(err);
    } finally {
      dispatch(finishItemsRequest({ streamId, contextKey, requestId }));
    }
  },
);

export const deleteVisualMedia = createAsyncThunk(
  "media/deleteVisualMedia",
  async ({ streamId, id }: { streamId: string; id: string }, { dispatch }) => {
    dispatch(setGetItemLoading(true));
    try {
      await VisualMediaInstance.deleteVisualMedia(streamId, id);
      dispatch(pruneDeletedMedia([id]));
      dispatch(refreshStreamSnapshot(streamId));
    } catch (err: any) {
      console.log({ err });
      dispatchErrorNotification(err);
    }
    dispatch(setGetItemLoading(false));
  },
);

/** The api's MAX_DELETE_BATCH (api/src/visualMedia/dto/DeleteMediaBatch.dto.ts). */
const MAX_DELETE_BATCH = 500;

/** UC-5.11: one cleanup operation per chunk of at most 500 ids. */
export const deleteMediaBatch = createAsyncThunk(
  "media/deleteMediaBatch",
  async (
    { streamId, ids, type }: { streamId: string; ids: string[]; type: FolderTypeEnum },
    { dispatch },
  ) => {
    if (!ids.length) return;
    dispatch(setGetItemLoading(true));
    let deleted = false;
    try {
      for (let start = 0; start < ids.length; start += MAX_DELETE_BATCH) {
        const chunk = ids.slice(start, start + MAX_DELETE_BATCH);
        if (type === FolderTypeEnum.audio) await AudioInstance.deleteAudioBatch(streamId, chunk);
        else await VisualMediaInstance.deleteVisualMediaBatch(streamId, chunk);
        // Prune each accepted chunk at once, so a later failure keeps what was deleted.
        dispatch(pruneDeletedMedia(chunk));
        deleted = true;
      }
    } catch (err: any) {
      dispatchErrorNotification(err);
    }
    if (deleted) dispatch(refreshStreamSnapshot(streamId));
    dispatch(setGetItemLoading(false));
  },
);

export const getBackgroundList = createAsyncThunk(
  "media/getBackgroundList",
  async (streamId: string, { dispatch }) => {
    dispatch(setBackgroundsLoading(true));
    try {
      const response = await VisualMediaInstance.getVisualMediaList(streamId, {
        query: "",
        pageNumber: 1,
        classification: VisualMediaClassificationEnum.background,
      });
      dispatch(setBackgrounds(response.data.data));
    } catch (err: any) {
      dispatchErrorNotification(err);
    }
    dispatch(setBackgroundsLoading(false));
  },
);

export const uploadBackground = createAsyncThunk(
  "media/uploadBackground",
  async (
    { streamId, body }: { streamId: string; body: File },
    { dispatch },
  ) => {
    dispatch(setBackgroundsLoading(true));
    const id = uuid();
    const controller = new AbortController();
    const uploader = new Uploader({
      requestKey: id,
      streamId,
      fileName: body.name,
      file: body,
      contentType: body.type,
    });
    const unregister = registerLiveUpload(uploader.lifetime, controller);
    dispatch(addPendingUpload({ id, streamId, body, progress: 0, controller }));
    dispatch(updateUploadStatus({ id, status: "uploading" }));
    uploader.lifetime.onCancellation = (result) =>
      dispatch(setUploadLifetime({ id, ...result }));
    let abort = () => {};
    let saving = false;
    try {
      const background = await new Promise<IVisualMedia>((resolve, reject) => {
        abort = () => {
          void uploader.abort();
          if (!saving) reject(new Error("Upload cancelled"));
        };
        controller.signal.addEventListener("abort", abort, { once: true });
        uploader.lifetime.onExpired = () => controller.abort();
        uploader
          .onComplete(async ({ Key }) => {
            saving = true;
            try {
              const response = await saveWithRetry(
                () => VisualMediaInstance.savePresigned(streamId, {
                  classification: VisualMediaClassificationEnum.background,
                  originalName: body.name,
                  Key,
                }),
                { signal: controller.signal },
              );
              uploader.lifetime.finish();
              resolve(response.data);
            } catch (error) {
              reject(error);
              throw error;
            }
          })
          .onProgress(({ percentage }) => {
            dispatch(setUploadBackgroundProgress(percentage));
            dispatch(updateUploadItemProgress({ id, progress: percentage }));
          })
          .onError(reject);
        uploader.start();
      });
      dispatch(updateUploadStatus({ id, status: "completed" }));
      dispatch(addBackgroundToList(background));
      dispatch(setSelectedBackgroundId(background.id));
      dispatch(setUploadBackgroundProgress(0));
      pollForMediaThumbnail("background", streamId, background.id, dispatch);
    } catch (err: any) {
      dispatch(
        updateUploadStatus({
          id,
          status: controller.signal.aborted ? "cancelled" : "failed",
        }),
      );
      if (!controller.signal.aborted) dispatchErrorNotification(err);
    } finally {
      uploader.lifetime.stop();
      unregister();
      controller.signal.removeEventListener("abort", abort);
      dispatch(setBackgroundsLoading(false));
    }
  },
);

export const deleteBackground = createAsyncThunk(
  "media/deleteBackground",
  async ({ streamId, id }: { streamId: string; id: string }, { dispatch }) => {
    dispatch(setBackgroundsLoading(true));
    try {
      await VisualMediaInstance.deleteVisualMedia(streamId, id);
      dispatch(onDeleteBackgroundSuccess(id));
      dispatch(refreshStreamSnapshot(streamId));
    } catch (err: any) {
      console.log({ err });
      dispatchErrorNotification(err);
    }
    dispatch(setBackgroundsLoading(false));
  },
);

/**
 * Re-run a failed Basic encode, then refresh the library row.
 *
 * The api may reconcile a lost encode (ENCODE_RECOVERY_EXHAUSTED with no
 * terminal observation) before it will start a new generation, so the first
 * Retry may not actually restart the encode. Only claim success once the
 * refreshed row shows queued/running; otherwise tell the user we're still
 * checking the previous attempt.
 */
export const retryEncode = createAsyncThunk(
  "media/retryEncode",
  async ({ streamId, id }: { streamId: string; id: string }, { dispatch }) => {
    try {
      await VisualMediaInstance.retryEncode(streamId, id);
      try {
        const latest = await VisualMediaInstance.getVisualMediaById(streamId, id);
        dispatch(setItem(latest.data));
        const status = (latest.data as IVisualMedia).encodeStatus;
        if (status === EncodeStatusEnum.queued || status === EncodeStatusEnum.running) {
          notification.success({ message: i18n.t("media:library.encodeRetryStarted") });
        } else {
          notification.info({ message: i18n.t("media:library.encodeRetryChecking") });
        }
      } catch {
        // The retry POST already succeeded; a failed refresh doesn't mean it
        // didn't start, so don't show an error toast for it (client Minor 2).
        notification.info({ message: i18n.t("media:library.encodeRetryChecking") });
      }
    } catch (err) {
      dispatchErrorNotification(err);
    }
  },
);

export const uploadOverlay = createAsyncThunk(
  "media/uploadOverlay",
  async (
    { streamId, body }: { streamId: string; body: File },
    { dispatch },
  ) => {
    dispatch(setOverlaysLoading(true));
    const id = uuid();
    const controller = new AbortController();
    dispatch(addPendingUpload({ id, streamId, body, progress: 0, controller }));
    if (body.size > MAX_FILE_SIZE_BYTES) {
      dispatch(updateUploadStatus({ id, status: "failed" }));
      notification.error({ message: i18n.t("errors:UPLOAD_FILE_TOO_LARGE") });
      dispatch(setOverlaysLoading(false));
      return;
    }
    const lifetime = new UploadLifetime({
      requestKey: id,
      streamId,
      name: body.name,
      size: body.size,
      contentType: body.type,
      state: "active",
    });
    const unregister = registerLiveUpload(lifetime, controller);
    dispatch(updateUploadStatus({ id, status: "uploading" }));
    lifetime.onCancellation = (result) =>
      dispatch(setUploadLifetime({ id, ...result }));
    lifetime.onExpired = () => controller.abort();
    const abort = () => {
      void lifetime.cancel();
    };
    controller.signal.addEventListener("abort", abort, { once: true });
    try {
      const presignedUrl = await VisualMediaInstance.generatePresignedUrl(
        streamId,
        {
          requestKey: lifetime.record.requestKey,
          fileName: body.name,
          contentType: body.type,
          contentLength: body.size,
        },
      );
      lifetime.bind(presignedUrl.data.intentId, presignedUrl.data.Key);
      dispatch(setUploadLifetime({ id, intentId: presignedUrl.data.intentId }));
      if (controller.signal.aborted) {
        await lifetime.cancel();
        throw new Error("Upload cancelled");
      }
      await axios.put(presignedUrl.data.url, body, {
        signal: controller.signal,
        timeout: 1000 * 60 * 60 * 10,
        headers: {
          "Content-Type": body.type,
        },
      });
      if (controller.signal.aborted) throw new Error("Upload cancelled");
      const data = await saveWithRetry(
        () => VisualMediaInstance.savePresigned(streamId, {
          classification: VisualMediaClassificationEnum.overlay,
          originalName: body.name,
          Key: presignedUrl.data.Key,
        }),
        { signal: controller.signal },
      );

      lifetime.finish();
      dispatch(updateUploadStatus({ id, status: "completed" }));
      dispatch(updateUploadItemProgress({ id, progress: 100 }));
      dispatch(refreshStreamSnapshot(streamId));
      dispatch(setOverlaysLoading(false));
      return data.data;
    } catch (err) {
      if (!controller.signal.aborted) await lifetime.cancel();
      dispatch(
        updateUploadStatus({
          id,
          status: controller.signal.aborted ? "cancelled" : "failed",
        }),
      );
      dispatch(setOverlaysLoading(false));
      throw err;
    } finally {
      lifetime.stop();
      unregister();
      controller.signal.removeEventListener("abort", abort);
    }
  },
);

export const getVisualMediaById = createAsyncThunk(
  "media/getVisualMediaById",
  async ({ streamId, id, silent = false }: { streamId: string; id: string; silent?: boolean }, { dispatch }) => {
    try {
      const response = await VisualMediaInstance.getVisualMediaById(streamId, id);
      dispatch(setItem(response.data));
    } catch (err: any) {
      if (!silent) dispatchErrorNotification(err);
    }
  },
);

const POLL_INTERVAL_MS = 5000;
const MAX_POLL_ATTEMPTS = 10;

const pollForMediaThumbnail = (
  type: "audio" | "video" | "background",
  streamId: string,
  id: string,
  dispatch: any,
) => {
  let attempts = 0;

  const poll = async () => {
    attempts++;
    try {
      if (type === "audio") {
        const response = await AudioInstance.getAudioById(streamId, id);
        dispatch(setItem(response.data));
        // Audio items don't have thumbnails, so one fetch is enough
        return;
      } else {
        const response = await VisualMediaInstance.getVisualMediaById(streamId, id);
        dispatch(setItem(response.data));
        // A failed thumbnail is final too; the row shows that failure.
        const polled = response.data as IVisualMedia;
        if (polled.thubmnailUrl || polled.thumbnailError) {
          return;
        }
      }
    } catch (err: any) {
      dispatchErrorNotification(err);
    }

    // Continue polling if thumbnail not yet available and under max attempts
    if (attempts < MAX_POLL_ATTEMPTS) {
      setTimeout(poll, POLL_INTERVAL_MS);
    }
  };

  dispatch(refreshStreamSnapshot(streamId));
  setTimeout(poll, POLL_INTERVAL_MS);
};
